// frontend/src/components/common/ErrorBoundary.jsx
//
// Global error boundary — catches unhandled React render errors
// and shows a recovery UI instead of a white screen.

import { Component } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import './ErrorBoundary.css';

function CrashScreen({ error, onReset }) {
  const { t } = useTranslation();
  // Bounded one-liner: the error's message string is short and
  // grep-able by support. Hide stack traces — those are dev-console
  // territory and can leak implementation detail. JavaScript can throw any
  // value, a string or null included, and that value is the message then.
  const errorMessage = error instanceof Error ? error.message : String(error);
  const truncated = errorMessage.length > 200
    ? `${errorMessage.slice(0, 200)}…`
    : errorMessage;
  return (
    <div className="error-boundary">
      <h1 className="error-boundary__title">{t('common.errorHeading')}</h1>
      <p className="error-boundary__message">
        <Trans
          i18nKey="errorBoundary.message"
          components={{
            support: <a href="mailto:support@manexys.com" target="_blank" rel="noopener noreferrer" />,
            docs: <a href="https://help.manexys.com" target="_blank" rel="noopener noreferrer" />,
          }}
        />
      </p>
      {truncated && (
        // Always show the message (prod + dev). Support agents and
        // devs use it as a search key when triaging reports.
        <p className="error-boundary__error-code">
          <strong>{t('errorBoundary.errorLabel')}</strong> <code>{truncated}</code>
        </p>
      )}
      <div className="error-boundary__actions">
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t('errorBoundary.refreshPage')}
        </Button>
        <Button variant="outlined" onClick={onReset}>
          {t('errorBoundary.tryAgain')}
        </Button>
      </div>
      {import.meta.env.DEV && error && (
        <pre className="error-boundary__details">
          {error.toString()}
          {error.stack && `\n\n${error.stack}`}
        </pre>
      )}
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log both the error and the React component stack so devs can find
    // the failing component without source maps. Production users won't
    // see this; it's for the browser console.
    console.error(
      'ErrorBoundary caught an error:',
      error,
      '\nComponent stack:',
      errorInfo.componentStack,
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <CrashScreen error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
